import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalizedLoaded,
  type RouteLocationNormalizedTyped,
  type RouteRecordRaw,
} from "vue-router"
import { useSupervisorLoginDrawer } from "@/composables/useSupervisorLoginDrawer"

import MainLayout from "@/layouts/MainLayout.vue"
import TillLayout from "@/layouts/TillLayout.vue"

import SimplePayment from "@/views/SimplePayment.vue"

import QuickTill from "@/views/QuickTill.vue"
import FullTill from "@/views/FullTill.vue"

import OrderIndex from "@/views/OrderIndex.vue"
import OrderDetail from "@/views/OrderDetail.vue"

import ReportsIndex from "@/views/ReportsIndex.vue"

import MoreMenu from "@/views/MoreMenu.vue"

import SettingsProductIndex from "@/views/settings/SettingsProductIndex.vue"
import SettingsProductCreate from "@/views/settings/SettingsProductCreate.vue"
import SettingsProductEdit from "@/views/settings/SettingsProductEdit.vue"

import SettingsCategoryIndex from "@/views/settings/SettingsCategoryIndex.vue"
import SettingsCategoryCreate from "@/views/settings/SettingsCategoryCreate.vue"
import SettingsCategoryEdit from "@/views/settings/SettingsCategoryEdit.vue"

import SettingsCashierIndex from "@/views/settings/SettingsCashierIndex.vue"
import SettingsCashierCreate from "@/views/settings/SettingsCashierCreate.vue"
import SettingsCashierEdit from "@/views/settings/SettingsCashierEdit.vue"

import SettingsAdvancedOptions from "@/views/settings/SettingsAdvancedOptions.vue"
import SettingsBusinessFunding from "@/views/settings/SettingsBusinessFunding.vue"
import SettingsTransactions from "@/views/settings/SettingsTransactions.vue"
import SettingsServiceCharge from "@/views/settings/SettingsServiceCharge.vue"
import SettingsZReportHistory from "@/views/settings/SettingsZReportHistory.vue"

import LoginLayout from "@/layouts/LoginLayout.vue"
import LogInIndex from "@/views/LogInIndex.vue"

import OnboardingWizard from "@/views/OnboardingWizard.vue"

import "vue-router"
import type { RouteNamedMap } from "./RouteNamedMap"
import ZReportDetail from "@/views/ZReportDetail.vue"

declare module "vue-router" {
  interface RouteMeta {
    requiresSupervisor?: boolean
    getRequiresSupervisor?: () => boolean
  }
  interface TypesConfig {
    RouteNamedMap: RouteNamedMap
  }
}

const { isProductsEnabled, accessControlRestrict } =
  useTenant()
const { isSupervisor, setElevatedToken } = useAuth()
const { reveal: revealSupervisorLoginDrawer } =
  useSupervisorLoginDrawer()
const { defaultTillRoute } = useDefaultTillRoute()

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: MainLayout,
    children: [
      {
        path: "/",
        name: "home",
        redirect: () => {
          return !isProductsEnabled.value
            ? { name: "simplePayment" }
            : defaultTillRoute.value === "fullTill"
              ? { name: "fullTill" }
              : { name: "quickTill" }
        },
      },
      {
        path: "/till",
        children: [
          {
            path: "simple-payment",
            name: "simplePayment",
            component: SimplePayment,
          },
          {
            path: "",
            component: TillLayout,
            children: [
              {
                path: "quick-till",
                name: "quickTill",
                component: QuickTill,
              },
              {
                path: "full-till/:parentCategoryId?",
                name: "fullTill",
                component: FullTill,
                props: true,
              },
            ],
          },
        ],
      },
      {
        path: "/reports",
        name: "reportsIndex",
        component: ReportsIndex,
        meta: {
          getRequiresSupervisor: () =>
            accessControlRestrict.value.reports,
        },
      },
      {
        path: "/orders",
        name: "orderIndex",
        component: OrderIndex,
        meta: {
          getRequiresSupervisor: () =>
            accessControlRestrict.value.transactions,
        },
      },
      {
        path: "/orders/:orderId",
        name: "orderDetail",
        component: OrderDetail,
        props: true,
      },
      {
        path: "/settings",
        children: [
          {
            path: "",
            name: "moreMenu",
            component: MoreMenu,
          },
          {
            path: "products",
            children: [
              {
                path: "",
                name: "settingsProductIndex",
                component: SettingsProductIndex,
                meta: {
                  requiresSupervisor: true,
                },
              },
              {
                path: "new",
                name: "settingsProductCreate",
                component: SettingsProductCreate,
                meta: {
                  requiresSupervisor: true,
                },
              },
              {
                path: ":productId/edit",
                name: "settingsProductEdit",
                component: SettingsProductEdit,
                props: true,
                meta: {
                  requiresSupervisor: true,
                },
              },
            ],
          },
          {
            path: "categories",
            children: [
              {
                path: "",
                name: "settingsCategoryIndex",
                component: SettingsCategoryIndex,
                meta: {
                  requiresSupervisor: true,
                },
              },
              {
                path: "new",
                name: "settingsCategoryCreate",
                component: SettingsCategoryCreate,
                meta: {
                  requiresSupervisor: true,
                },
              },
              {
                path: ":categoryId/edit",
                name: "settingsCategoryEdit",
                component: SettingsCategoryEdit,
                props: true,
                meta: {
                  requiresSupervisor: true,
                },
              },
            ],
          },
          {
            path: "cashiers",
            children: [
              {
                path: "",
                name: "settingsCashierIndex",
                component: SettingsCashierIndex,
                meta: {
                  requiresSupervisor: true,
                },
              },
              {
                path: "new",
                name: "settingsCashierCreate",
                component: SettingsCashierCreate,
                meta: {
                  requiresSupervisor: true,
                },
              },
              {
                path: ":cashierId/edit",
                name: "settingsCashierEdit",
                component: SettingsCashierEdit,
                props: true,
                meta: {
                  requiresSupervisor: true,
                },
              },
            ],
          },
          {
            path: "advanced-options",
            name: "settingsAdvancedOptions",
            component: SettingsAdvancedOptions,
            meta: {
              requiresSupervisor: true,
            },
          },
          {
            path: "transactions",
            name: "settingsTransactions",
            component: SettingsTransactions,
          },
          {
            path: "business-funding",
            name: "settingsBusinessFunding",
            component: SettingsBusinessFunding,
            meta: {
              requiresSupervisor: true,
            },
          },
          {
            path: "service-charge",
            name: "settingsServiceCharge",
            component: SettingsServiceCharge,
            meta: {
              requiresSupervisor: true,
            },
          },
          {
            path: "z-report-history",
            name: "settingsZReportHistory",
            component: SettingsZReportHistory,
          },
          {
            path: "z-report-history/:zReportId",
            name: "settingsZReportDetail",
            component: ZReportDetail,
            props: (
              route: RouteLocationNormalizedTyped<
                RouteNamedMap,
                "settingsZReportDetail"
              >
            ) => ({
              zReportId: route.params.zReportId,
              previousPage: history.state?.backRoute,
            }),
          },
        ],
      },
    ],
  },
  {
    path: "/post-payment-order-detail/:orderId",
    name: "postPaymentOrderDetail",
    component: OrderDetail,
    props: (
      route: RouteLocationNormalizedLoaded<"postPaymentOrderDetail">
    ) => {
      return {
        previousPage: route.query?.previous_page,
        orderId: route.params.orderId,
      }
    },
  },
  {
    path: "/login",
    component: LoginLayout,
    children: [
      {
        path: "",
        name: "logInIndex",
        component: LogInIndex,
      },
    ],
  },
  {
    path: "/onboarding",
    name: "onboarding",
    component: OnboardingWizard,
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
})

router.beforeEach(async (to) => {
  const requiresSupervisor =
    to.meta.requiresSupervisor ||
    (to.meta.getRequiresSupervisor &&
      to.meta.getRequiresSupervisor())
  if (!isSupervisor.value && requiresSupervisor) {
    const { isCanceled } =
      await revealSupervisorLoginDrawer()

    return !isCanceled
  } else if (!requiresSupervisor) {
    setElevatedToken(null)
  }
})

export default router
